import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
} from "react";

interface ThemeContextType {
  theme: "light" | "dark";
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/** Root <html> canvas color per theme — mirrors --color-page-subtle. */
const ROOT_BG: Record<"light" | "dark", string> = {
  light: "#f2f4f8",
  dark: "#0d0f1c",
};

/**
 * Applies the theme to the document: data-theme on <body> (what the token
 * stylesheets and app CSS key off) plus color-scheme and an explicit
 * background on the root <html> element. The <html> canvas is the surface
 * painted behind <body> on the first frame of a hard reload; it can't be
 * themed with tokens (they're scoped to the [data-theme] element, which is
 * <body>), so it's set directly and kept in sync here and in index.html's
 * pre-paint inline script — preventing the white flash on reload.
 */
function applyTheme(next: "light" | "dark") {
  document.body.setAttribute("data-theme", next);
  document.documentElement.style.colorScheme = next;
  document.documentElement.style.backgroundColor = ROOT_BG[next];
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<"light" | "dark">("dark");
  // Synchronous mirror of the active theme. Written before every DOM update so
  // the enforcing observer (below) compares against the intended theme and
  // never fights a legitimate change.
  const themeRef = useRef<"light" | "dark">("dark");

  // Single place that mutates the DOM for a theme change: keeps the ref, the
  // <body> data-theme, and the <html> color-scheme/background in lockstep.
  const setThemeEverywhere = (next: "light" | "dark") => {
    themeRef.current = next;
    setTheme(next);
    applyTheme(next);
  };

  useEffect(() => {
    const savedTheme =
      (localStorage.getItem("theme") as "light" | "dark") || "dark";
    setThemeEverywhere(savedTheme);

    // The host owns theming, but every MFE renders into this same <body>.
    // On MFE→MFE navigation a mounting/unmounting MFE can drop or overwrite
    // data-theme; when it's absent the design tokens fall back to the light
    // :root set, flashing the content area white (the token is the only color
    // source). Re-assert the host theme the instant that happens. Mutation
    // observer callbacks run as a microtask before the browser paints, so the
    // wrong-theme frame is corrected without any visible flash.
    const observer = new MutationObserver(() => {
      if (document.body.getAttribute("data-theme") !== themeRef.current) {
        applyTheme(themeRef.current);
      }
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    // Listen for theme changes from other tabs (cross-tab sync)
    const handleStorageChange = (e: StorageEvent) => {
      if (
        e.key === "theme" &&
        (e.newValue === "light" || e.newValue === "dark")
      )
        setThemeEverywhere(e.newValue);
    };

    // Listen for theme changes from other apps in the same window (same-window sync)
    const handleThemeChange = (e: CustomEvent) => {
      const newTheme = e.detail;
      if (newTheme === "light" || newTheme === "dark")
        setThemeEverywhere(newTheme);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("themechange", handleThemeChange as EventListener);
    return () => {
      observer.disconnect();
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "themechange",
        handleThemeChange as EventListener,
      );
    };
  }, []);

  const toggleTheme = () => {
    const newTheme = themeRef.current === "light" ? "dark" : "light";
    setThemeEverywhere(newTheme);
    localStorage.setItem("theme", newTheme);

    // Dispatch custom event to notify other apps in the same window
    window.dispatchEvent(new CustomEvent("themechange", { detail: newTheme }));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
